/**
 * Which workflow stage a list's row actions are looked up under, and which URL segment its links
 * are built from. They are not always the same thing, and conflating them is what made PGSS-5369
 * hard to fix safely.
 *
 * Actions are keyed on (stage, row status) together, and the stage has always been derived from
 * the URL. That is ambiguous for one screen: `/in-production` serves BOTH the Orders View and the
 * Shipment View, switched by a filter rather than a route. Both resolved to 'in-production', so
 * both got the shipment action set -- which for a supplier there is Edit/Delete Shipment, Export,
 * Print and Mark as Completed and Shipped.
 *
 * The Orders View gets its own stage rather than borrowing 'order'. 'order' is already the
 * supplier's Received Orders stage (drawing requests, Move to Production) and is reachable at
 * /orders, so configuring this screen there would surface its actions on an unrelated list.
 * 'inproduction-orders' already exists as StageConstants.INPRODUCTION_ORDERS and is used by
 * OrderQueryService for exactly this view, so the name is the system's own, not a new invention.
 */
export const ORDERS_VIEW_FILTER = 'inproduction_orders_view';
export const INPRODUCTION_ORDERS_STAGE = 'inproduction-orders';
/** Supplier Shipped Orders → Orders View (`/fulfilled` with this filter). */
export const SHIPPED_ORDERS_VIEW_FILTER = 'shipped_orders_view';

const SELF_NAMED_STAGES = [
  'in-production', 'fulfilled', 'rfd', 'completed', 'confirmed', 'container-management',
];
const SHIPMENT_STAGES = ['outbound', 'order-shipments', 'container-shipments'];

export const getActionStage = (type, viewFilter) => {
  if (type === 'supplier-invoices') return 'invoice';
  // Must be tested BEFORE the self-named list, which would otherwise claim 'in-production'.
  if (type === 'in-production' && viewFilter === ORDERS_VIEW_FILTER) return INPRODUCTION_ORDERS_STAGE;
  if (SELF_NAMED_STAGES.includes(type)) return type;
  if (SHIPMENT_STAGES.includes(type)) return 'shipment';
  return typeof type === 'string' ? type.slice(0, -1) : '';
};

/**
 * The URL segment a row action links to, where it differs from the stage.
 *
 * Links are built as `/{stage}s/{id}`, which only works while the stage happens to be the route.
 * It does not here -- 'inproduction-orders' would give `/inproduction-orderss/`. The rows are
 * orders and `/orders/:orderId` already exists, so that is where they go.
 *
 * Returns null when the existing behaviour is correct, so no other screen moves.
 */
export const getActionRouteBase = (stage) =>
  (stage === INPRODUCTION_ORDERS_STAGE ? 'orders' : null);
